import type { ConsumptionSettingsResponse, ConsumptionSummaryResponse } from '@protoss/shared';
import * as React from 'react';
import { consumptionApi } from '../services/consumptionApi';

export interface UseConsumptionDataResult {
  settings: ConsumptionSettingsResponse | null;
  summary: ConsumptionSummaryResponse | null;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  syncError: string | null;
  reload: () => void;
  triggerSync: () => Promise<void>;
}

export function useConsumptionData(projectUuid: string): UseConsumptionDataResult {
  const [settings, setSettings] = React.useState<ConsumptionSettingsResponse | null>(null);
  const [summary, setSummary] = React.useState<ConsumptionSummaryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [syncError, setSyncError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fetchedSettings, fetchedSummary] = await Promise.all([
        consumptionApi.getSettings(projectUuid),
        consumptionApi.getSummary(projectUuid),
      ]);
      setSettings(fetchedSettings);
      setSummary(fetchedSummary);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectUuid]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = React.useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await consumptionApi.sync(projectUuid);
      // Reload summary data after sync
      const [fetchedSettings, fetchedSummary] = await Promise.all([
        consumptionApi.getSettings(projectUuid),
        consumptionApi.getSummary(projectUuid),
      ]);
      setSettings(fetchedSettings);
      setSummary(fetchedSummary);
    } catch (err) {
      setSyncError((err as Error).message);
    } finally {
      setSyncing(false);
    }
  }, [projectUuid]);

  return {
    settings,
    summary,
    loading,
    syncing,
    error,
    syncError,
    reload: () => void load(),
    triggerSync,
  };
}
